import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Loader2 } from 'lucide-react';

import BudgetMembersList from '@/components/organisms/budget/BudgetMembersList';
import InviteMemberModal from '@/components/molecules/budget/InviteMemberModal';
import BudgetMemberCalculationsFooter from '@/components/molecules/budget/BudgetMemberCalculationsFooter';
import MembersSectionHeader from '@/components/molecules/budget/MembersSectionHeader';
import InfoModal from '@/components/molecules/InfoModal';
import { Button } from '@/components/ui/button';
import {
  budgetMembersEditFormSchema,
  getMembersEditStepFieldNames,
  toApiMemberType,
  type BudgetMemberFormValues,
  type BudgetMembersEditFormValues
} from '@/components/organisms/budget/form-steps/budgetStep.schema';
import { useNavigationGuard } from '@/hooks/useNavigationGuard';
import { useInviteBudgetMembers } from '@/hooks/useInviteBudgetMembers';
import { budgetService } from '@/services/budget.service';
import { budgetMemberService } from '@/services/budget-member.service';
import { BulkUpdateMemberItem } from '@/services/types/budget-members.types';
import { getCurrentUser } from '@/store/slices/auth';
import { useBudgetPermissions } from '@/hooks/useBudgetPermissions';
import { getBudgetMemberCalculations, type BudgetMemberCalculations } from '@/utils/budgetCalculations';
import profileDiscardIcon from '@/assets/icons/profile-discard.svg';
import MobileAppBar from '@/components/molecules/MobileAppBar';

const MembersEditPage = () => {
  const navigate = useNavigate();
  const { budgetId } = useParams<{ budgetId: string }>();
  const currentUser = useSelector(getCurrentUser);
  const { can } = useBudgetPermissions({ budgetId });

  const canInviteMembers = can('member:invite');
  const canUpdateMemberRole = can('member:updateRole');
  const canRemoveMembers = can('member:remove');

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bmev, setBmev] = useState(0);
  const [currency, setCurrency] = useState('EUR');
  const [memberCalculations, setMemberCalculations] = useState<BudgetMemberCalculations | null>(null);
  const [isInviteSuccessModalOpen, setIsInviteSuccessModalOpen] = useState(false);
  const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null);

  const {
    register,
    watch,
    setValue,
    control,
    handleSubmit,
    reset,
    trigger,
    getValues,
    formState: { errors, isDirty }
  } = useForm<BudgetMembersEditFormValues>({
    resolver: zodResolver(budgetMembersEditFormSchema),
    defaultValues: {
      members: []
    },
    mode: 'onChange'
  });

  const { fields, remove } = useFieldArray({
    control,
    name: 'members'
  });

  const { isDiscardModalOpen, handleCancel, handleDiscardChanges, handleDiscardModalClose, allowNavigation } =
    useNavigationGuard({
      isDirty,
      fallbackPath: `/budgets/${budgetId}/members`,
      // This page is always pushed from the Members list page above, so
      // cancelling should pop back to it rather than replace (which would
      // create a duplicate consecutive history entry and break the back button).
      backOnCancel: true
    });

  const fetchBudgetMembers = async (options?: { silent?: boolean }) => {
    if (!budgetId) {
      setLoadError('Budget ID is missing');
      if (!options?.silent) {
        setIsLoading(false);
      }
      return;
    }

    try {
      if (!options?.silent) {
        setIsLoading(true);
        setLoadError(null);
      }

      const [budgetResponse, membersResponse] = await Promise.all([
        budgetService.getBudget(budgetId),
        budgetMemberService.getMembers(budgetId)
      ]);

      setBmev(Number(budgetResponse.data.bmev) || 0);
      setCurrency(budgetResponse.data.currency ?? 'EUR');

      const members = (membersResponse.data ?? []).map((m) => ({
        memberId: m._id,
        userId: m.user_id,
        email: m.email,
        memberName: m.name,
        profilePicUrl: m.profile_pic_url,
        type: m.type ? m.type.toLowerCase() : '',
        oreHours: m.ore_hours,
        hourlyRate: m.hourly_rate,
        role: (m.role === 'OWNER' ? 'owner' : m.role === 'VALIDATOR' ? 'validation' : 'staff') as
          | 'owner'
          | 'validation'
          | 'staff'
      }));

      reset({ members });
    } catch (error: any) {
      console.error('Error loading budget members:', error);
      if (options?.silent) {
        toast.error(error?.message ?? 'Failed to refresh members. Please try again.');
      } else {
        setLoadError(error?.message ?? 'Failed to load members. Please try again.');
      }
    } finally {
      if (!options?.silent) {
        setIsLoading(false);
      }
    }
  };

  const { isInviteModalOpen, setIsInviteModalOpen, isInviting, handleInviteMembers } = useInviteBudgetMembers(
    budgetId,
    {
      onSuccess: () => setIsInviteSuccessModalOpen(true),
      refreshMembers: () => fetchBudgetMembers({ silent: true })
    }
  );

  useEffect(() => {
    fetchBudgetMembers();
  }, [budgetId]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const emitCalculations = (values: BudgetMembersEditFormValues) => {
      const activeMembers = (values.members ?? []).filter(
        (member): member is BudgetMemberFormValues => member.role !== 'remove_access'
      );
      setMemberCalculations(getBudgetMemberCalculations(activeMembers, bmev, currency));
    };

    emitCalculations(getValues());

    const subscription = watch((value) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        emitCalculations(value as BudgetMembersEditFormValues);
      }, 300);
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [watch, getValues, bmev, currency]);

  const handleMemberRoleChange = async (index: number, memberId: string, role: 'validation' | 'staff') => {
    if (!budgetId) {
      toast.error('Budget ID is missing');
      return;
    }

    const previousRole = getValues(`members.${index}.role`);
    if (previousRole === role) {
      return;
    }

    setUpdatingMemberId(memberId);
    setValue(`members.${index}.role`, role, { shouldDirty: false, shouldValidate: true });

    try {
      await budgetMemberService.updateMemberRole(budgetId, memberId, {
        role: role === 'validation' ? 'VALIDATOR' : 'STAFF'
      });
      toast.success('Member role updated');
    } catch (error: any) {
      console.error('Error updating member role:', error);
      setValue(`members.${index}.role`, previousRole, { shouldDirty: false, shouldValidate: true });
      toast.error(error?.message ?? 'Failed to update member role. Please try again.');
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleRemoveMember = async (index: number, memberId: string) => {
    if (!budgetId) {
      toast.error('Budget ID is missing');
      return;
    }

    setUpdatingMemberId(memberId);

    try {
      await budgetMemberService.removeMember(budgetId, memberId);
      remove(index);
      toast.success('Member removed successfully');
    } catch (error: any) {
      console.error('Error removing member:', error);
      toast.error(error?.message ?? 'Failed to remove member. Please try again.');
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleSave = async (data: BudgetMembersEditFormValues) => {
    if (!budgetId) {
      toast.error('Budget ID is missing');
      return;
    }

    if (!isDirty) {
      toast.error('No changes to save');
      return;
    }

    const membersWithId = (data.members ?? []).filter((member) => member.memberId);
    const memberUpdates: BulkUpdateMemberItem[] = membersWithId.map((member) => ({
      _id: member.memberId,
      type: member.type ? toApiMemberType(member.type) : undefined,
      ore_hours: Number(member.oreHours) || 0
    }));

    if (memberUpdates.length === 0) {
      toast.error('No changes to save');
      return;
    }

    try {
      setIsSubmitting(true);

      await budgetMemberService.updateMembers(budgetId, {
        members: memberUpdates
      });

      toast.success('Members saved successfully');
      allowNavigation();
      // This page was pushed from the Members list page, so pop back to it
      // instead of replacing — replacing to the same URL as the previous
      // history entry would create a duplicate entry and break the back button.
      navigate(-1);
    } catch (error: any) {
      console.error('Error saving members:', error);
      toast.error(error?.message ?? 'Failed to save members. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveClick = async () => {
    const members = getValues('members') ?? [];
    const isValid = await trigger(getMembersEditStepFieldNames(members) as Parameters<typeof trigger>[0]);

    if (!isValid) {
      toast.error('Please fill all required member fields');
      return;
    }

    handleSubmit(handleSave)();
  };

  if (isLoading) {
    return (
      <div className="border flex-1 rounded-[16px] p-4 bg-white min-h-[320px] flex items-center justify-center gap-2">
        <Loader2 className="w-6 h-6 animate-spin text-secondary" />
        <p className="text-secondary text-sm">Loading members...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="border flex-1 rounded-[16px] p-4 bg-white min-h-[320px] flex flex-col items-center justify-center gap-4">
        <p className="text-red-500 text-sm">{loadError}</p>
        <Button className="rounded-full" variant="primary-reverse" type="button" onClick={() => fetchBudgetMembers()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <>
      <MobileAppBar title="Manage Member" />

      <div className="md:border flex-1 flex flex-col rounded-[16px] p-4 px-5 md:bg-white space-y-6">
        <MembersSectionHeader
          memberCount={fields.length}
          onInviteClick={canInviteMembers ? () => setIsInviteModalOpen(true) : undefined}
        />

        <form id="members-edit-form" className="flex-1" onSubmit={handleSubmit(handleSave)}>
          <BudgetMembersList
            variant="edit"
            register={register}
            watch={watch}
            setValue={setValue}
            errors={errors}
            fields={fields}
            remove={remove}
            currentUserEmail={currentUser?.email}
            currency={currency}
            updatingMemberId={updatingMemberId}
            onRoleChange={canUpdateMemberRole ? handleMemberRoleChange : undefined}
            onRemoveMember={canRemoveMembers ? handleRemoveMember : undefined}
          />
        </form>

        <footer className="flex flex-col md:flex-row items-end justify-between gap-4 px-4 py-7 md:bg-gradient-to-b from-[#F0EFFD]/0 from-0% to-[#F0EFFD] to-[17.74%] md:bg-white md:bg-none md:from-transparent md:to-transparent">
          <div className="flex-1 min-w-0 w-full md:w-auto">
            <BudgetMemberCalculationsFooter calculations={memberCalculations} />
          </div>
          <div className="md:flex md:justify-end md:gap-4 shrink-0 w-full md:w-auto">
            <Button
              className="hidden md:block rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit text-[#E8507E]"
              variant="text"
              onClick={handleCancel}
              disabled={isSubmitting}
              type="button"
            >
              Cancel
            </Button>
            <Button
              className="rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit"
              onClick={handleSaveClick}
              disabled={isSubmitting}
              type="button"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </span>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </footer>
      </div>

      {isDiscardModalOpen && (
        <InfoModal
          isOpen={isDiscardModalOpen}
          variant="confirm"
          title="Are you sure you want to discard changes?"
          message="Unsaved changes will not be saved"
          buttonText="Discard"
          onButtonClick={handleDiscardChanges}
          icon={profileDiscardIcon}
          onClose={handleDiscardModalClose}
        />
      )}

      <InviteMemberModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInvite={handleInviteMembers}
        loading={isInviting}
      />

      <InfoModal
        isOpen={isInviteSuccessModalOpen}
        variant="success"
        title="Successfully Invite"
        message="Let's wait until they enter the Budget"
        buttonText="Complete"
        onButtonClick={() => setIsInviteSuccessModalOpen(false)}
        icon=""
      />
    </>
  );
};

export default MembersEditPage;
